import { organizationModule } from '@/store/organization/module'
import { instructorModule } from '@/store/instructor/module'
import { integrationsModule } from '@/store/integrations/module'
import { examModule } from '@/store/exam/module'
import {
    type CreateLMSIntegration,
    type FetchLMSIntegrations,
    type DeleteLMSIntegration,
    type UpsertLMSCourseIntegration,
    type FetchLMSCourseIntegrations,
    type ToggleLMSCourseIntegration,
} from '@/store/integrations/types'
import { fetchLibLoadable, resetLoadable } from '@/loadingUtils'
import { runCloudFunction } from '@/store/ParseUtils'
import { groupModule } from '@/store/group/module'

const createLMSIntegration = async (params: {
    clientId: string
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('createLMSIntegration: Could not find currentOrganization')
    }

    const createdLMSIntegration = await runCloudFunction<CreateLMSIntegration>('createLMSIntegration', {
        clientId: params.clientId,
        organizationId: currentOrganization.objectId,
        name: currentOrganization.name,
        lms: 'canvas',
    })

    if (createdLMSIntegration) {
        resetLoadable(integrationsModule.state.integrations[createdLMSIntegration?.objectId])
    }

    await fetchLMSIntegrations()

}

const fetchLMSIntegrations = async (forceFetch?: boolean) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error(
            'fetchLMSIntegrations: Could not find currentOrganization')
    }

    return fetchLibLoadable(
        integrationsModule,
        'integrations',
        currentOrganization.objectId,
        async () => {
            return runCloudFunction<FetchLMSIntegrations>('fetchLMSIntegrations', {
                organizationId: currentOrganization.objectId,
            })
        },
        forceFetch)
}

const fetchLMSCourseIntegrations = async (forceFetch?: boolean) => {
    const currentOrganization = organizationModule.getters.getCurrentOrganization()

    const currentIntegrations = integrationsModule.getters.getCurrentIntegrations()

    if (!currentOrganization || !currentIntegrations) {
        throw new Error(`fetchLMSCourseIntegrations:
        Could not find currentOrgInstructor and/or currentOrganization and/or currentIntegrations`)
    }

    /*
        For now this is always assuming only one integration (since we have no mechanism
        to swap integrations for now
    */
    if (currentIntegrations.length) {
        const lmsId = currentIntegrations[0]?.objectId
        if (!lmsId) {
            return
        }

        await Promise.all([
            examModule.actions.fetchCurrentOrgExams(),
            integrationsModule.actions.fetchLMSIntegrations(),
            groupModule.actions.fetchCurrentOrgGroups(),
            instructorModule.actions.fetchCurrentOrgInstructors(),
            instructorModule.actions.fetchCurrentOrgInstructorUsers(),
        ])

        return fetchLibLoadable(
            integrationsModule,
            'courseIntegrations',
            currentOrganization.objectId,
            async () => {
                return runCloudFunction<FetchLMSCourseIntegrations>('fetchLMSCourseIntegrations', {
                    organizationId: currentOrganization.objectId,
                    lmsId,
                })
            },
            forceFetch)
    }
}

const upsertLMSCourseIntegration = async (params: {
    deploymentId: string
    lmsId: string
    orgGroupIds: string[]
    lmsCourseIntegrationId?: string
    courseName: string
}) => {
    const currentOrgInstructor = instructorModule.getters.getCurrentOrgInstructor()
    const currentOrganization = organizationModule.getters.getCurrentOrganization()

    if (!currentOrgInstructor || !currentOrganization) {
        throw new Error('upsertLMSCourseIntegration: Could not find currentOrgInstructor and/or currentOrganization')
    }

    const {
        deploymentId,
        lmsId,
        orgGroupIds,
        lmsCourseIntegrationId,
        courseName,
    } = params

    await runCloudFunction<UpsertLMSCourseIntegration>('upsertLMSCourseIntegration', {
        deploymentId,
        lmsId,
        name: courseName,
        orgInstructorId: currentOrgInstructor.objectId,
        orgGroupIds,
        organizationId: currentOrganization.objectId,
        ...(lmsCourseIntegrationId && { lmsCourseIntegrationId }),
    })

    await Promise.all([
        integrationsModule.actions.fetchLMSCourseIntegrations(true),
        groupModule.actions.fetchCurrentOrgGroups(true),
    ])
}

const deleteLMSIntegration = async (params: {
    lmsId: string
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('deleteLMSIntegration: Could not find currentOrganization')
    }

    await runCloudFunction<DeleteLMSIntegration>('deleteLMSIntegration', {
        lmsId: params.lmsId,
        organizationId: currentOrganization.objectId,
    })

    resetLoadable(integrationsModule.state.courseIntegrations[currentOrganization.objectId])

    await Promise.all([
        fetchLMSIntegrations(),
        fetchLMSCourseIntegrations(),
    ])
}

const toggleLMSCourseIntegration = async (params: {
    lmsCourseIntegrationId: string
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('toggleLMSIntegration: Could not find currentOrganization')
    }

    await runCloudFunction<ToggleLMSCourseIntegration>('toggleLMSCourseIntegration', {
        lmsCourseIntegrationId: params.lmsCourseIntegrationId,
        organizationId: currentOrganization.objectId,
    })

    await fetchLMSCourseIntegrations(true)
}

export default {
    createLMSIntegration,
    upsertLMSCourseIntegration,
    deleteLMSIntegration,
    fetchLMSIntegrations,
    fetchLMSCourseIntegrations,
    toggleLMSCourseIntegration,
}
